import { Attachment } from "@/src/components/ui-kit/attachment-viewer";
import { useAuth } from "@/src/contexts/auth.context";
import { usePermissions } from "@/src/hooks/use-permissions";
import { BlocksApiNews } from "@/src/models/news.model";
import gqlClient from "@/src/services/api/graphql.client";
import { useGlobalNewsQuery } from "@/src/services/api/news.api";
import {
  useDeleteNews,
  useNewsCategoriesQuery,
  useNewsSaveState,
  useTogglePinNews,
} from "@/src/services/api/news/news.hooks";
import { GET_NEWS_ITEM } from "@/src/services/api/news/news.query";
import {
  useMarkNewsRead,
  useToggleSaved,
} from "@/src/services/profile/profile.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const themeColors = {
  light: {
    slateDefault: "#526581",
    onyxDefault: "#22272a",
    chalkDefault: "#FFFFFF",
    obsidianDefault: "#F2F4F6",
    ash3: "#DFE5EC",
  },
  dark: {
    slateDefault: "#8aaac8",
    onyxDefault: "#D7DADC",
    chalkDefault: "#141a1f",
    obsidianDefault: "#0f1113",
    ash3: "#2d3540",
  },
} as const;

export function useNewsDetail() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { canUpdate, canDelete, isSuperAdmin } = usePermissions();
  const userId = user?.itemId ?? "";
  const { primaryPalette, resolvedScheme } = useTheme();
  const colors = themeColors[resolvedScheme];

  const { newsId } = useLocalSearchParams<{ newsId: string }>();

  const [article, setArticle] = useState<BlocksApiNews | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    if (!newsId) return;
    setIsLoading(true);
    setIsError(false);
    gqlClient()
      .then((client) =>
        client.request(GET_NEWS_ITEM, {
          where: { ItemId: { eq: newsId }, IsDeleted: { neq: true } },
        }),
      )
      .then((data: any) => {
        setArticle(data?.getnewss?.items?.[0] ?? null);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("🔍 article fetch error:", err);
        setIsError(true);
        setIsLoading(false);
      });
  }, [newsId]);

  const { data: categories = [] } = useNewsCategoriesQuery();
  const { isSaved } = useNewsSaveState();
  const { mutate: toggleSave } = useToggleSaved(userId, "news");

  const { mutate: pinNews } = useTogglePinNews();

  // Opening an article marks it read exactly once per article. The `newsId`
  // guard (rather than a plain "has this effect run" flag) means navigating
  // from one article straight to another re-fires for the new id instead of
  // being permanently suppressed by the first open.
  const { mutate: markNewsRead } = useMarkNewsRead(userId);
  const markedReadNewsId = useRef<string | null>(null);

  useEffect(() => {
    if (!userId || !article?.ItemId) return;
    if (markedReadNewsId.current === article.ItemId) return;
    markedReadNewsId.current = article.ItemId;
    markNewsRead(article.ItemId);
  }, [userId, article?.ItemId, markNewsRead]);

  const {
    data: globalNewsData,
    isLoading: isLoadingGlobalNews,
    isError: isGlobalNewsError,
  } = useGlobalNewsQuery();

  const { mutate: deleteNews } = useDeleteNews({
    onSuccess: () => {
      setShowDeleteConfirm(false);
      router.back();
    },
  });

  const isOwner = !!userId && article?.CreatedBy === userId;

  const categoryNames = (article?.categoryIds ?? [])
    .map((id: string) => categories.find((cat) => cat.categoryID === id)?.name)
    .filter((name: string | undefined): name is string => !!name);

  const attachments: Attachment[] = (article?.attachmentUrls ?? []).map(
    (a) => ({
      url: a.url,
      type: a.mimeType?.startsWith("image/") ? "image" : "file",
    }),
  );

  const handleEdit = () => {
    if (!article) return;
    router.push({
      pathname: "/(tabs)/news/news-creation",
      params: {
        draftId: article.ItemId,
        Title: article.Title,
        Content: article.Content,
        categoryIds: JSON.stringify(article.categoryIds ?? []),
        coverImageUrl: article.coverImageUrl ?? "",
        attachments: JSON.stringify(article.attachmentUrls ?? []),
        // Without this the edit form's toggle read undefined, defaulted to
        // false, and saving wrote that back — silently unpinning the article.
        isPinned: String(article.isPinned ?? false),
        // Tells the creation screen whether this article is still a draft, so
        // it can hide "Save as Draft" when editing an already-published one.
        isDraft: String(article.isDraft ?? false),
      },
    });
  };

  const dropdownItems = article
    ? [
        {
          label: isSaved(article.ItemId) ? t("BTN_UNSAVE") : t("BTN_SAVE"),
          onPress: () => toggleSave(article.ItemId),
        },
        { label: t("LABEL_SHARE"), onPress: () => setShowShareModal(true) },
        ...(canUpdate("news")
          ? [
              {
                label: article.isPinned ? t("BTN_UNPIN") : t("BTN_PIN"),
                onPress: () =>
                  pinNews({
                    itemId: article.ItemId,
                    isPinned: !article.isPinned,
                  }),
              },
            ]
          : []),
        ...(isOwner && canUpdate("news")
          ? [{ label: t("BTN_EDIT"), onPress: handleEdit }]
          : []),
        ...((isOwner || isSuperAdmin) && canDelete("news")
          ? [
              {
                label: t("BTN_DELETE"),
                onPress: () => setShowDeleteConfirm(true),
                destructive: true,
              },
            ]
          : []),
      ]
    : [];

  return {
    t,
    article,
    isLoading,
    isError,
    primaryPalette,
    colors,
    resolvedScheme,
    categoryNames,
    attachments,
    dropdownItems,
    globalNewsData,
    isLoadingGlobalNews,
    isGlobalNewsError,
    showDeleteConfirm,
    setShowDeleteConfirm,
    showShareModal,
    setShowShareModal,
    deleteNews,
  };
}
